import { DateTime } from 'luxon'
import omitBy from 'lodash/omitBy'
import { captureException } from '@sentry/react'
import isNil from 'lodash/isNil'
import { money } from '@sellpy/commons'
import { useQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import { useCountry } from '../../../common/hooks/useCountry'
import { locale } from '../../../common/region/locale.js'
import { useCurrentUserId } from '../../../common/user/currentUser'
import { getItemsInCart } from '../../../common/entities/selectors/checkout.js'
import { getRegion } from '../../../common/region/region'
import {
  getChosenFreightAlternativeAndOption,
  getEstimatedShipping,
  calculateTotalFreightCost
} from '../utils/freight'
import { fastHandlingPriceQuery } from '../utils/fastHandlingPriceQuery.gql'
import { useCalculateTotalPriceForItems } from './useCalculateTotalPriceForItems.jsx'

const getDeliveries = ({ freightChoices, isFastHandlingSelected }) => {
  const deliveries = { p2p: { bySeller: {} } }

  if (!freightChoices) {
    captureException(new Error('In getDeliveries, but freightChoices is undefined!'))
    return deliveries
  }
  for (const orderGroup in freightChoices) {
    const { freightAlternative, freightOption } = getChosenFreightAlternativeAndOption({
      freightChoices,
      orderGroup
    })

    if (orderGroup === 'regular') {
      const optionKey = Object.keys(freightAlternative.freightOptions)[0]

      deliveries.regular = omitBy(
        {
          ...freightAlternative,
          price: money.toBaseUnit(freightAlternative.price),
          originalPrice: money.toBaseUnit(freightAlternative.originalPrice),
          isFastHandling: isFastHandlingSelected || undefined,
          [optionKey]: freightOption?.id,
          freightOptions: undefined,
          estimatedShipping: getEstimatedShipping(freightAlternative, isFastHandlingSelected)
        },
        isNil
      )
    } else {
      deliveries.p2p.bySeller[orderGroup] = {
        ...omitBy(freightAlternative, isNil),
        price: money.toBaseUnit(freightAlternative.price),
        originalPrice: money.toBaseUnit(freightAlternative.originalPrice)
      }
    }
  }
  return deliveries
}

export const transformToPaymentData = ({
  reservationIds,
  freightChoices,
  isFastHandlingSelected,
  fastHandlingPrice,
  totalPriceForItems,
  address,
  promoCode,
  containerOrder,
  email,
  country
}) => {
  const totalFreight = calculateTotalFreightCost({
    freightChoices,
    isFastHandlingSelected,
    fastHandlingPrice
  })

  return {
    totalAmount: money.add(totalPriceForItems.price, totalFreight.price),
    totalItemAmountWithoutPromoCode: totalPriceForItems.originalPrice,
    totalItemAmountPromoCodeDiscount: money.subtract(totalPriceForItems.originalPrice)(
      totalPriceForItems.price
    ),
    totalFreightAmount: totalFreight.price,
    address: address,
    validPromoCode: promoCode,
    containerOrder: containerOrder,
    email: email,
    reservationIds,
    deliveries: getDeliveries({ isFastHandlingSelected, freightChoices }),
    locale,
    region: process.env.REGION,
    returnUrl: `${window.location.origin}/checkout/payment`,
    origin: window.location.origin,
    country,
    version: 1
  }
}

const ITEM_ORDER_PAYMENT_STATE = 'checkoutItemOrderPaymentData'
const CACHE_TTL_DURATION = { hour: 1 }

// When we return from a payment provider, we want to restore the form state
export const useCacheItemOrderPaymentData = () => {
  const userId = useCurrentUserId()
  const items = useSelector((state) => getItemsInCart(state, { userId }))

  const totalPriceForItems = useCalculateTotalPriceForItems()
  const country = useCountry()

  const { data: { fastHandlingPrice } = {} } = useQuery(fastHandlingPriceQuery, {
    variables: { region: getRegion() }
  })

  return (state) => {
    const paymentData = transformToPaymentData({
      totalPriceForItems,
      address: state.address,
      promoCode: state.promoCode,
      containerOrder: state.containerOrder,
      email: state.email,
      reservationIds: Object.values(items.toJSON())
        .map((item) => item.reservations)
        .flat(),
      isFastHandlingSelected: state.isFastHandlingSelected,
      country,
      fastHandlingPrice,
      freightChoices: state.freightChoices
    })
    window.localStorage.setItem(
      ITEM_ORDER_PAYMENT_STATE,
      JSON.stringify({ state: paymentData, cachedAt: new Date() })
    )
  }
}

export const getCachedPaymentData = () => {
  try {
    const cache = window.localStorage.getItem(ITEM_ORDER_PAYMENT_STATE)
    if (!cache) return
    const { state, cachedAt } = JSON.parse(cache)

    const cacheIsExpired =
      DateTime.fromJSDate(new Date(cachedAt)).plus(CACHE_TTL_DURATION) < DateTime.local()
    if (cacheIsExpired) {
      clearCachedPaymentData()
      return
    }

    return state
  } catch {
    clearCachedPaymentData()
  }
}

export const clearCachedPaymentData = () => {
  window.localStorage.removeItem(ITEM_ORDER_PAYMENT_STATE)
}
